import React, { useState } from "react";
import { observer } from "mobx-react";
import copilotStore from "Stores/copilotStore";

// Raw field names as returned by enrich_seller_profile (DRS's
// SELLER_WEBSITE_PROMPT) — snake_case, some list-valued. Grouped into
// sections matching the design reference's SectionBlock layout.
// Review-first layout: a summary card (name, where, one-liner) the reader can
// confirm in a glance, then three sections. Sentence-length lists render as
// rows, short lists (customer names) as pills.
const SUMMARY_FIELDS = [
  { key: "company_name", label: "Company name" },
  { key: "website", label: "Website" },
  { key: "location", label: "Location" },
  { key: "product_summary", label: "One-line summary" }
];

const SECTIONS = [
  {
    title: "What you sell",
    fields: [
      { key: "product_name", label: "Product name" },
      { key: "what_it_does", label: "What it does" },
      { key: "main_value_prop", label: "Main value props", list: true },
      { key: "key_use_cases", label: "Key use cases", list: true }
    ]
  },
  {
    title: "Proof and positioning",
    fields: [
      { key: "current_customers", label: "Current customers", list: true },
      { key: "competitive_edge", label: "Competitive edge", list: true }
    ]
  },
  {
    title: "How you sell",
    fields: [
      { key: "goal_of_outreach", label: "Goal of outreach" },
      { key: "preferred_messaging_tone", label: "Messaging tone" },
      { key: "sales_motion_style", label: "Sales motion style" }
    ]
  }
];

// Above this length an item is a sentence, not a tag; pills wrap badly and
// stop reading as a list.
const PILL_MAX_CHARS = 36;

const asList = value => {
  if (Array.isArray(value)) return value.filter(Boolean);
  if (typeof value === "string" && value.trim()) return [value.trim()];
  return [];
};

const FieldRow = ({ label, value, onChange }) => {
  const [editing, setEditing] = useState(false);
  return (
    <div className="copilotSellerProfile__field">
      <div className="copilotSellerProfile__fieldLabel">{label}</div>
      <div className="copilotSellerProfile__fieldRow">
        {editing ? (
          <textarea
            className="copilotSellerProfile__fieldInput"
            value={value || ""}
            autoFocus
            onChange={event => onChange(event.target.value)}
            onBlur={() => setEditing(false)}
          />
        ) : (
          <span className="copilotSellerProfile__fieldValue">
            {value || <em>Not set</em>}
          </span>
        )}
        <i
          className="material-icons-outlined copilotSellerProfile__editIcon"
          onClick={() => setEditing(true)}
        >
          edit
        </i>
      </div>
    </div>
  );
};

const useListEdit = (items, onChange) => {
  const [draft, setDraft] = useState("");
  const remove = index => onChange(items.filter((_, i) => i !== index));
  const update = (index, text) =>
    onChange(items.map((item, i) => (i === index ? text : item)));
  const add = () => {
    const trimmed = draft.trim();
    if (!trimmed) return;
    onChange([...items, trimmed]);
    setDraft("");
  };
  const onDraftKey = event => {
    if (event.key === "Enter") {
      event.preventDefault();
      add();
    }
  };
  return { draft, setDraft, remove, update, add, onDraftKey };
};

// Short values (names, tags) as removable pills.
const PillList = ({ label, items, onChange }) => {
  const { draft, setDraft, remove, add, onDraftKey } = useListEdit(items, onChange);
  return (
    <div className="copilotSellerProfile__field">
      <div className="copilotSellerProfile__fieldLabel">
        {label}
        {items.length > 0 && <span className="copilotSellerProfile__count">{items.length}</span>}
      </div>
      <div className="copilotSellerProfile__pills">
        {items.map((item, index) => (
          <span className="copilotSellerProfile__pill" key={`${item}_${index}`}>
            {item}
            <i className="material-icons-outlined" onClick={() => remove(index)}>
              close
            </i>
          </span>
        ))}
        <input
          className="copilotSellerProfile__pillInput"
          placeholder="Add…"
          value={draft}
          onChange={event => setDraft(event.target.value)}
          onKeyDown={onDraftKey}
          onBlur={add}
        />
      </div>
    </div>
  );
};

// Sentence-length values as a readable list: one row each, click to edit,
// remove on hover. Reads like the brief it is, not like a tag cloud.
const RowList = ({ label, items, onChange }) => {
  const { draft, setDraft, remove, update, add, onDraftKey } = useListEdit(items, onChange);
  const [editing, setEditing] = useState(-1);
  return (
    <div className="copilotSellerProfile__field">
      <div className="copilotSellerProfile__fieldLabel">
        {label}
        {items.length > 0 && <span className="copilotSellerProfile__count">{items.length}</span>}
      </div>
      <ul className="copilotSellerProfile__rows">
        {items.map((item, index) => (
          <li className="copilotSellerProfile__row" key={`${index}_${item.slice(0, 12)}`}>
            <span className="copilotSellerProfile__rowBullet" />
            {editing === index ? (
              <textarea
                className="copilotSellerProfile__fieldInput"
                value={item}
                autoFocus
                onChange={event => update(index, event.target.value)}
                onBlur={() => setEditing(-1)}
              />
            ) : (
              <span className="copilotSellerProfile__rowText" onClick={() => setEditing(index)}>
                {item}
              </span>
            )}
            <i
              className="material-icons-outlined copilotSellerProfile__rowRemove"
              title="Remove"
              onClick={() => remove(index)}
            >
              close
            </i>
          </li>
        ))}
        <li className="copilotSellerProfile__row copilotSellerProfile__row--add">
          <span className="copilotSellerProfile__rowBullet" />
          <input
            className="copilotSellerProfile__rowInput"
            placeholder="Add another…"
            value={draft}
            onChange={event => setDraft(event.target.value)}
            onKeyDown={onDraftKey}
            onBlur={add}
          />
        </li>
      </ul>
    </div>
  );
};

const ListField = props => {
  const long = props.items.some(item => String(item).length > PILL_MAX_CHARS);
  return long ? <RowList {...props} /> : <PillList {...props} />;
};

/**
 * Right-side editable seller-profile draft (SG-2182) — same panel pattern
 * as SequencePreviewPanel, opened via copilotStore.previewPanel /
 * activePreviewBlock. Unlike the sequence preview (view-only), this panel
 * is fully editable: edits write directly onto the observable block object
 * (block.profile), not local-only state, so they survive closing and
 * reopening the panel. Approve/discard act on whatever's currently there.
 */
const BUILD_STAGES = [
  "Reading the website…",
  "Identifying what you sell…",
  "Mapping your target audience…",
  "Drafting positioning & value props…"
];

const SellerProfilePreviewPanel = observer(() => {
  const block = copilotStore.activePreviewBlock;
  if (!block) return null;
  const building = block.status === "building";
  const profile = block.profile || {};

  const setField = key => value => {
    block.profile = { ...(block.profile || {}), [key]: value };
  };
  const setListField = key => list => {
    block.profile = { ...(block.profile || {}), [key]: list };
  };

  const approve = () => copilotStore.approveSellerProfile(block.id, block.profile || {});
  const discard = () => copilotStore.discardSellerProfile(block.id);

  return (
    <div className="copilotTablePanel copilotPreviewPanel copilotSellerPanel">
      <div className="copilotTablePanel__header">
        <div className="copilotTablePanel__headMeta">
          <div className="copilotTablePanel__title">
            {building ? <span className="copilotLiveCard__liveDot" /> : <i className="material-icons-outlined">storefront</i>}
            Seller profile draft
          </div>
          <div className="copilotTablePanel__count">
            {building
              ? "Researching in realtime…"
              : profile.website
                ? `Sourced from ${profile.website}`
                : "Review and edit before saving"}
          </div>
        </div>
        <span
          className="material-icons-outlined copilotTablePanel__close"
          onClick={copilotStore.closePreviewPanel}
        >
          close
        </span>
      </div>
      {!block.done && !building && (
        <div className="copilotPreviewPanel__banner copilotSellerPanel__banner">
          <i className="material-icons-outlined">edit</i>
          <span>Review the draft. Click any value to edit it, then save.</span>
        </div>
      )}
      <div className="copilotTablePanel__body copilotPreviewPanel__body">
        {building ? (
          <div className="copilotSellerBuild">
            {(() => {
              const currentIndex = Math.max(BUILD_STAGES.indexOf(block.stage), 0);
              return BUILD_STAGES.map((label, index) => {
                const isDone = index < currentIndex;
                const isCurrent = index === currentIndex;
                return (
                  <div
                    key={label}
                    className={`copilotSellerBuild__row ${isCurrent ? "is-current" : ""} ${isDone ? "is-done" : ""}`}
                  >
                    <span className="copilotSellerBuild__marker">
                      {isDone ? (
                        <i className="material-icons-outlined">check_circle</i>
                      ) : isCurrent ? (
                        <span className="copilotSellerBuild__spinner" />
                      ) : (
                        <span className="copilotSellerBuild__dot" />
                      )}
                    </span>
                    <span className="copilotSellerBuild__label">{label}</span>
                  </div>
                );
              });
            })()}
            <div className="copilotSeqPreview__typing">
              <span className="copilotDot" />
              <span className="copilotDot" />
              <span className="copilotDot" />
              <span className="copilotSeqPreview__typingLabel">{block.stage || "Researching…"}</span>
            </div>
          </div>
        ) : block.done ? (
          <div className="copilotSeqPreview__done">
            <i className="material-icons-outlined">
              {block.discarded ? "delete_outline" : block.saved === false ? "error_outline" : "check_circle"}
            </i>
            <span>
              {block.discarded
                ? "Discarded. No seller profile was saved."
                : block.saved === false
                  ? "Seller profile save failed."
                  : "Seller profile saved."}
            </span>
          </div>
        ) : (
          <div className="copilotSellerProfile__body">
            <div className="copilotSellerProfile__section copilotSellerProfile__section--summary">
              {SUMMARY_FIELDS.map(f => (
                <FieldRow key={f.key} label={f.label} value={profile[f.key]} onChange={setField(f.key)} />
              ))}
            </div>
            {SECTIONS.map(section => (
              <div className="copilotSellerProfile__section" key={section.title}>
                <div className="copilotSellerProfile__sectionTitle">{section.title}</div>
                {section.fields.map(f =>
                  f.list ? (
                    <ListField
                      key={f.key}
                      label={f.label}
                      items={asList(profile[f.key])}
                      onChange={setListField(f.key)}
                    />
                  ) : (
                    <FieldRow key={f.key} label={f.label} value={profile[f.key]} onChange={setField(f.key)} />
                  )
                )}
              </div>
            ))}
          </div>
        )}
      </div>
      {!block.done && !building && (
        <div className="copilotSellerProfile__footer">
          <button type="button" className="copilotSellerProfile__approveBtn" onClick={approve}>
            <i className="material-icons-outlined">check</i>
            Looks right, save
          </button>
          <button type="button" className="copilotSellerProfile__discardBtn" onClick={discard}>
            <i className="material-icons-outlined">close</i>
            Discard
          </button>
        </div>
      )}
    </div>
  );
});

export default SellerProfilePreviewPanel;
